import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { IconLock } from '@tabler/icons-react';

export function useReveal<T extends HTMLElement>(delay = 0) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reveal = () => {
      el.classList.add('is-visible');
      el.querySelectorAll<HTMLElement>('.home-reveal').forEach((c) => c.classList.add('is-visible'));
    };
    if (typeof IntersectionObserver === 'undefined') { reveal(); return; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (delay) setTimeout(reveal, delay); else reveal();
        io.disconnect();
      }
    }, { rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return ref;
}

export const BAND: React.CSSProperties = { background: 'var(--warm-card)', borderTop: '1px solid var(--warm-border)', borderBottom: '1px solid var(--warm-border)' };

export function Section({ children, className = '', id, style }: { children: React.ReactNode; className?: string; id?: string; style?: React.CSSProperties }) {
  return (
    <section id={id} className={`py-24 md:py-32 ${className}`} style={style}>
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        {children}
      </div>
    </section>
  );
}

/** A Section whose content blurs/fades in when it scrolls into view. */
export function RevealSection({ children, className, id, style, innerClassName = '' }: {
  children: React.ReactNode; className?: string; id?: string; style?: React.CSSProperties; innerClassName?: string;
}) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <Section className={className} id={id} style={style}>
      <div ref={ref} className={`home-reveal ${innerClassName}`}>{children}</div>
    </Section>
  );
}

export function Eyebrow({ children }: { children: React.ReactNode }) {
  return <p className="warm-eyebrow mb-4">{children}</p>;
}

export function H2({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <h2 className={`font-display font-light text-[2rem] md:text-[2.75rem] leading-tight tracking-tight ${className}`} style={{ color: 'var(--warm-text)' }}>
      {children}
    </h2>
  );
}

export function Lead({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <p className={`mt-4 text-lg max-w-xl leading-relaxed ${className}`} style={{ color: 'var(--warm-muted)' }}>{children}</p>;
}

export function Muted({ children }: { children: React.ReactNode }) {
  return <span style={{ color: 'var(--warm-muted)' }}>{children}</span>;
}

export function Btn({ to, children, className = '' }: { to: string; children: React.ReactNode; className?: string }) {
  return (
    <Link to={to} className={`warm-btn inline-flex items-center gap-1.5 px-5 py-2.5 text-sm ${className}`}>
      {children}
    </Link>
  );
}

export function TextLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: 'var(--warm-text)' }}>
      {children} <ArrowRight className="size-3.5" />
    </Link>
  );
}

export function IconTile({ icon: Icon, bg = 'var(--warm-page)', fg = 'var(--warm-text)' }: { icon: typeof IconLock; bg?: string; fg?: string }) {
  return (
    <span className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] shrink-0" style={{ background: bg, border: '1px solid var(--warm-border)', color: fg }}>
      <Icon size={18} stroke={1.5} />
    </span>
  );
}

export function Chip({ children, tone = 'neutral', wrap = false }: { children: React.ReactNode; tone?: 'neutral' | 'green' | 'amber' | 'red' | 'blue' | 'dark'; wrap?: boolean }) {
  const tones: Record<string, React.CSSProperties> = {
    neutral: { background: 'var(--warm-page)', color: 'var(--warm-muted)', border: '1px solid var(--warm-border)' },
    green: { background: 'var(--chip-green-bg)', color: 'var(--chip-green)' },
    amber: { background: 'var(--chip-amber-bg)', color: 'var(--chip-amber)' },
    red: { background: 'var(--chip-red-bg)', color: 'var(--chip-red)' },
    blue: { background: 'var(--chip-blue-bg)', color: 'var(--chip-blue)' },
    dark: { background: 'var(--warm-inv)', color: 'var(--warm-inv-text)' },
  };
  return <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 font-medium ${wrap ? 'rounded-xl leading-snug' : 'rounded-full whitespace-nowrap'}`} style={tones[tone]}>{children}</span>;
}
